import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, message, Typography, Space, Spin } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import BrandLogo from '../../components/common/BrandLogo';
import { setPasswordForCurrentUser } from '../../api/auth';

const { Title } = Typography;

export const SetPassword = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(['auth']);
  const { firebaseUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
        <Spin size="large" tip={t('auth:loadingSession')} />
      </div>
    );
  }

  if (!firebaseUser) {
    return <Navigate to="/login" replace />;
  }

  const onFinish = async (values: { password: string; confirmPassword: string }) => {
    if (values.password !== values.confirmPassword) {
      message.error(t('auth:passwordsDoNotMatch'));
      return;
    }
    try {
      setLoading(true);
      await setPasswordForCurrentUser(values.password);
      message.success(t('auth:passwordSetSuccess'));
      navigate('/select-workspace', { replace: true });
    } catch (e) {
      const code = (e as { code?: string })?.code;
      if (code === 'auth/weak-password') {
        message.error(t('auth:registerWeakPassword'));
      } else {
        message.error(t('auth:passwordSetError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f2f5',
      }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
        <Card style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <BrandLogo variant="bright" showTagline={true} />
          </div>
          <Title level={4} style={{ margin: 0, fontWeight: 'normal', color: '#666' }}>
            {t('auth:setPasswordTitle')}
          </Title>
          <p style={{ color: '#666', marginTop: 8, marginBottom: 24 }}>
            {t('auth:setPasswordSubtitle')}
          </p>
          <Form
            form={form}
            name="set-password"
            onFinish={onFinish}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="password"
              label={t('auth:password')}
              rules={[
                { required: true, message: t('auth:passwordRequired') },
                { min: 6, message: t('auth:passwordMinLength') },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('auth:passwordPlaceholder')}
              />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label={t('auth:confirmPassword')}
              dependencies={['password']}
              rules={[
                { required: true, message: t('auth:confirmPasswordRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('auth:passwordsDoNotMatch')));
                  },
                }),
              ]}
              hasFeedback
              validateTrigger="onBlur"
              validateFirst
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('auth:confirmPasswordPlaceholder')}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {t('auth:createPassword')}
            </Button>
          </Form.Item>
          </Form>
        </Card>
      </Space>
    </div>
  );
};
